const STORAGE_KEY = "global_search_last_session";

/** @typedef {{
 *   query: string,
 *   target: string,
 *   searched: boolean,
 *   results: object[],
 *   pagination: object|null,
 *   searchMeta: object|null,
 *   facilityKey: string,
 *   savedAt: number,
 * }} GlobalSearchSession
 */

/** @type {GlobalSearchSession|null} */
let memorySession = null;

export function getFacilityKeyForSession(facilityId) {
  return facilityId != null && String(facilityId).trim() !== ""
    ? String(facilityId)
    : "none";
}

/**
 * @param {string} [facilityKey]
 * @returns {GlobalSearchSession|null}
 */
export function loadGlobalSearchSession(facilityKey) {
  const pick = (s) => {
    if (!s?.searched || !Array.isArray(s.results)) return null;
    if (facilityKey != null && s.facilityKey !== facilityKey) return null;
    return s;
  };

  const fromMemory = pick(memorySession);
  if (fromMemory) return fromMemory;

  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const session = pick(parsed);
    if (session) memorySession = session;
    return session;
  } catch {
    return null;
  }
}

/** @param {GlobalSearchSession} session */
export function saveGlobalSearchSession(session) {
  if (!session?.searched) return;
  memorySession = session;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    /* ignore quota */
  }
}

export function clearGlobalSearchSession() {
  memorySession = null;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * @param {{
 *   query: string,
 *   target: string,
 *   searched: boolean,
 *   results: object[],
 *   pagination?: object|null,
 *   searchMeta?: object|null,
 *   facilityKey: string,
 * }} snapshot
 */
export function buildGlobalSearchSession(snapshot) {
  return {
    query: snapshot.query ?? "",
    target: snapshot.target ?? "enrollees",
    searched: !!snapshot.searched,
    results: Array.isArray(snapshot.results) ? snapshot.results : [],
    pagination: snapshot.pagination ?? null,
    searchMeta: snapshot.searchMeta ?? null,
    facilityKey: snapshot.facilityKey ?? "none",
    savedAt: Date.now(),
  };
}
